import { onAuthStateChanged, signOut as fbSignOut, type User } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { createStore } from '../app/store';
import { listDevice, unlistDevice } from './device';

/**
 * Who's signed in, and whether they may use the control room: only a business's owner (`riders/{uid}.role` is
 * `businessOwner`, and the business is keyed by their uid). Anyone else is told why, in plain words.
 */
export type Gate =
  | { state: 'loading' }
  | { state: 'signedOut' }
  | { state: 'checking'; user: User; offline?: boolean }
  | { state: 'notOwner'; user: User; why: 'noAccount' | 'courier' | 'inBusiness' | 'unreadable' }
  /** Signed in with no Routr account yet, or one that never chose a role: they can create their business here. */
  | { state: 'newOwner'; user: User }
  | { state: 'owner'; user: User; name: string | null };

/** [elsewhere]: this browser was just signed out from another device on the account (device.ts). */
export const session = createStore<{ gate: Gate; elsewhere: boolean }>({ gate: { state: 'loading' }, elsewhere: false });

let stopProfile: (() => void) | null = null;
let stopDevice: (() => void) | null = null;

onAuthStateChanged(auth, (user) => {
  stopProfile?.();
  stopProfile = null;
  stopDevice?.();
  stopDevice = null;
  if (!user) return session.set({ gate: { state: 'signedOut' } });
  session.set({ gate: { state: 'checking', user }, elsewhere: false });
  stopDevice = listDevice(user.uid, () => {
    session.set({ elsewhere: true });
    void fbSignOut(auth);
  });
  stopProfile = onSnapshot(
    doc(db, 'riders', user.uid),
    (snap) => {
      const d = snap.data();
      // Offline, Firestore answers from its empty cache: no proof there's no account, so wait for the server.
      if (!d && snap.metadata.fromCache) return session.set({ gate: { state: 'checking', user, offline: true } });
      if (!d || (d.role == null && (d.businessId ?? user.uid) === user.uid)) return session.set({ gate: { state: 'newOwner', user } });
      if (d.role === 'businessOwner' && (d.businessId ?? user.uid) === user.uid) {
        return session.set({ gate: { state: 'owner', user, name: typeof d.name === 'string' ? d.name : null } });
      }
      session.set({ gate: { state: 'notOwner', user, why: d.businessId && d.businessId !== user.uid ? 'inBusiness' : 'courier' } });
    },
    () => session.set({ gate: { state: 'notOwner', user, why: 'unreadable' } }),
  );
});

/** Signing out here: this browser off the account's list of devices first, while still signed in to say so. */
export const signOut = async () => {
  const uid = auth.currentUser?.uid;
  if (uid) await unlistDevice(uid);
  await fbSignOut(auth);
};

/** The account in words: its email, its phone number, or that it has neither. */
export const accountWords = (u: User) => u.email ?? u.phoneNumber ?? 'this account';
